import type { MetricPolarity } from '@convometry/contracts';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  CircleDashed,
  Clock,
  HelpCircle,
  Minus,
  PlugZap,
  ShieldCheck,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { EMPTY, formatNumber, formatRelative } from '../../lib/format.js';
import { Card } from '../layout/card.js';
import { Skeleton } from '../primitives/states.js';
import { Tooltip } from '../primitives/overlay.js';

// ───────── MetricCard ─────────

/**
 * - ok: value is a real measurement (0 is a legitimate value).
 * - no_data: nothing to measure in the period (renders "Sin datos", never 0).
 * - unknown: cannot be determined (e.g. coverage gap).
 * - insufficient: measured sample too small / not assessable.
 */
export type MetricState = 'ok' | 'loading' | 'no_data' | 'unknown' | 'insufficient';

export interface MetricCardProps {
  label: string;
  /** null/undefined = not measured. Never pass 0 for unknown. */
  value: number | null | undefined;
  format?: (value: number) => string;
  state?: MetricState;
  icon?: React.ReactNode;
  /** Definition shown in a tooltip (what is measured and how). */
  definition?: string;
  /** Denominator / sample line, e.g. "Mediana de 38 episodios respondidos". */
  sample?: React.ReactNode;
  /** Secondary value line (e.g. "P90 3 h 10 min"). */
  secondary?: React.ReactNode;
  /** Inline trend next to the value (legacy pill style). Prefer `comparison`. */
  trend?: React.ReactNode;
  /** Comparison line rendered under the value (e.g. <TrendLine />). */
  comparison?: React.ReactNode;
  /** Coverage slot (CoverageIndicator). Pass it only when coverage needs attention. */
  coverage?: React.ReactNode;
  /** Freshness slot (FreshnessLabel). */
  freshness?: React.ReactNode;
  className?: string;
}

const STATE_TEXT: Record<Exclude<MetricState, 'ok' | 'loading'>, { text: string; hint: string }> = {
  no_data: { text: 'Sin datos', hint: 'No hay observaciones en este periodo.' },
  unknown: { text: 'Desconocido', hint: 'No es posible determinarlo con la cobertura disponible.' },
  insufficient: { text: 'Muestra insuficiente', hint: 'No hay suficientes observaciones confiables.' },
};

export function MetricCard({
  label,
  value,
  format = (v) => formatNumber(v),
  state,
  icon,
  definition,
  sample,
  secondary,
  trend,
  comparison,
  coverage,
  freshness,
  className,
}: MetricCardProps) {
  const missing = value === null || value === undefined;
  // An "ok" state with a missing value is still rendered as no data — never as zero.
  const effective: MetricState = state === 'loading' ? 'loading' : missing ? (state && state !== 'ok' ? state : 'no_data') : (state ?? 'ok');
  const fallback = effective === 'ok' || effective === 'loading' ? 'no_data' : effective;
  const labelId = React.useId();
  return (
    <Card aria-labelledby={labelId} className={cn('flex flex-col gap-3', className)}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {icon ? (
            <span className="flex size-9 shrink-0 items-center justify-center rounded-control bg-brand-tint text-brand-primary [&_svg]:size-[18px]">
              {icon}
            </span>
          ) : null}
          <h3 id={labelId} className="text-body font-medium text-text-secondary">
            {label}
          </h3>
        </div>
        {definition ? (
          <Tooltip content={definition}>
            <button
              type="button"
              aria-label={`Definición: ${label}`}
              className="inline-flex size-7 shrink-0 items-center justify-center rounded-control text-text-muted hover:bg-surface-subtle hover:text-text-primary focus-visible:outline-2 focus-visible:outline-focus"
            >
              <HelpCircle aria-hidden className="size-4" />
            </button>
          </Tooltip>
        ) : null}
      </div>
      <div className="flex flex-wrap items-end gap-x-3 gap-y-1">
        {effective === 'loading' ? (
          <Skeleton label={`Cargando ${label}`} className="h-10 w-28" />
        ) : effective === 'ok' && value !== null && value !== undefined ? (
          <p className="cm-tabular text-kpi font-semibold tracking-[-0.02em] text-text-primary" data-testid="metric-value">
            {format(value)}
          </p>
        ) : (
          <p className="flex items-center gap-2 py-1.5" data-testid="metric-value">
            <span aria-hidden className="text-kpi font-semibold leading-none text-text-muted">
              {EMPTY}
            </span>
            <span className="text-body font-medium text-text-secondary">{STATE_TEXT[fallback].text}</span>
          </p>
        )}
        {trend && effective === 'ok' ? <div className="pb-1.5">{trend}</div> : null}
      </div>
      {comparison && effective === 'ok' ? <div className="-mt-1">{comparison}</div> : null}
      {secondary ? <div className="cm-tabular text-body text-text-secondary">{secondary}</div> : null}
      {sample ? <div className="cm-tabular text-helper text-text-secondary">{sample}</div> : null}
      {effective !== 'ok' && effective !== 'loading' && !sample ? (
        <p className="text-helper text-text-secondary">{STATE_TEXT[fallback].hint}</p>
      ) : null}
      {coverage || freshness ? (
        <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-border-subtle pt-3">
          {coverage}
          {freshness}
        </div>
      ) : null}
    </Card>
  );
}

// ───────── TrendBadge ─────────

export interface TrendBadgeProps {
  current: number | null | undefined;
  previous: number | null | undefined;
  polarity: MetricPolarity;
  /** percent (default) or absolute difference using formatDelta. */
  mode?: 'percent' | 'absolute';
  formatDelta?: (delta: number) => string;
  comparisonLabel?: string;
  className?: string;
}

export type TrendOutcome = 'improved' | 'worsened' | 'neutral' | 'unchanged' | 'unavailable';

export function trendOutcome(current: number | null | undefined, previous: number | null | undefined, polarity: MetricPolarity): TrendOutcome {
  if (current === null || current === undefined || previous === null || previous === undefined) return 'unavailable';
  if (current === previous) return 'unchanged';
  if (polarity === 'neutral') return 'neutral';
  const up = current > previous;
  return (up && polarity === 'higher_is_better') || (!up && polarity === 'lower_is_better') ? 'improved' : 'worsened';
}

export function TrendBadge({
  current,
  previous,
  polarity,
  mode = 'percent',
  formatDelta = (d) => formatNumber(d, { maximumFractionDigits: 1 }),
  comparisonLabel = 'vs. periodo anterior',
  className,
}: TrendBadgeProps) {
  const outcome = trendOutcome(current, previous, polarity);
  if (outcome === 'unavailable' || (mode === 'percent' && previous === 0 && current !== 0)) {
    return (
      <span className={cn('inline-flex items-center gap-1 rounded-pill border border-border-subtle bg-surface-subtle px-2 py-0.5 text-helper text-text-secondary', className)}>
        <Minus aria-hidden className="size-3.5" />
        Sin comparación
      </span>
    );
  }
  const c = current as number;
  const p = previous as number;
  const delta = c - p;
  const text =
    outcome === 'unchanged'
      ? 'Sin cambio'
      : mode === 'percent'
        ? `${delta > 0 ? '+' : '−'}${formatNumber(Math.abs(delta / p) * 100, { maximumFractionDigits: 1 })} %`
        : `${delta > 0 ? '+' : '−'}${formatDelta(Math.abs(delta))}`;
  const Icon = outcome === 'unchanged' ? Minus : delta > 0 ? TrendingUp : TrendingDown;
  const tone =
    outcome === 'improved'
      ? 'border-success/25 bg-success-bg text-success'
      : outcome === 'worsened'
        ? 'border-danger/25 bg-danger-bg text-danger'
        : 'border-border-subtle bg-surface-subtle text-text-secondary';
  const meaning = outcome === 'improved' ? 'mejora' : outcome === 'worsened' ? 'empeora' : 'sin interpretación de mejora';
  return (
    <span
      data-outcome={outcome}
      className={cn('cm-tabular inline-flex items-center gap-1 whitespace-nowrap rounded-pill border px-2 py-0.5 text-helper font-medium', tone, className)}
    >
      <Icon aria-hidden className="size-3.5" />
      {text}
      <span className="sr-only">
        {comparisonLabel} ({meaning})
      </span>
      <span aria-hidden className="font-normal opacity-80">
        {' '}
        {comparisonLabel}
      </span>
    </span>
  );
}

// ───────── TrendLine ─────────

export interface TrendLineProps {
  current: number | null | undefined;
  previous: number | null | undefined;
  polarity: MetricPolarity;
  comparisonLabel?: string;
  /** Text when there is nothing to compare against; null hides the line entirely. */
  unavailableText?: string | null;
  className?: string;
}

/**
 * Quiet comparison line for KPI cards: "↓ 18% vs. periodo anterior". Colour carries meaning only when the
 * metric has a polarity (improved = success, worsened = danger); neutral metrics stay grey. Always text + icon.
 */
export function TrendLine({
  current,
  previous,
  polarity,
  comparisonLabel = 'vs. periodo anterior',
  unavailableText = 'Sin datos del periodo anterior',
  className,
}: TrendLineProps) {
  const outcome = trendOutcome(current, previous, polarity);
  if (outcome === 'unavailable' || previous === 0) {
    if (unavailableText === null) return null;
    return <p className={cn('text-helper text-text-muted', className)}>{unavailableText}</p>;
  }
  const c = current as number;
  const p = previous as number;
  if (outcome === 'unchanged') {
    return <p className={cn('text-helper text-text-secondary', className)}>Sin cambio {comparisonLabel}</p>;
  }
  const up = c > p;
  const pct = Math.round(Math.abs((c - p) / p) * 100);
  const Icon = up ? ArrowUp : ArrowDown;
  const tone = outcome === 'improved' ? 'text-success' : outcome === 'worsened' ? 'text-danger' : 'text-text-secondary';
  const meaning = outcome === 'improved' ? 'mejora' : outcome === 'worsened' ? 'empeora' : 'sin interpretación de mejora';
  return (
    <p data-outcome={outcome} className={cn('cm-tabular inline-flex items-center gap-1 text-helper', className)}>
      <span className={cn('inline-flex items-center gap-0.5 font-medium', tone)}>
        <Icon aria-hidden className="size-3.5" />
        {pct === 0 ? '<1' : pct}%
      </span>
      <span className="text-text-secondary">{comparisonLabel}</span>
      <span className="sr-only">({meaning})</span>
    </p>
  );
}

// ───────── Coverage & freshness ─────────

export type CoverageStatus = 'complete' | 'partial' | 'uncertain' | 'disconnected';

const COVERAGE: Record<CoverageStatus, { label: string; icon: React.ReactNode; tone: string }> = {
  complete: { label: 'Cobertura completa', icon: <ShieldCheck aria-hidden />, tone: 'text-success' },
  partial: { label: 'Cobertura parcial', icon: <AlertTriangle aria-hidden />, tone: 'text-warning' },
  uncertain: { label: 'Cobertura incierta', icon: <CircleDashed aria-hidden />, tone: 'text-warning' },
  disconnected: { label: 'Número desconectado', icon: <PlugZap aria-hidden />, tone: 'text-danger' },
};

export interface CoverageIndicatorProps {
  status: CoverageStatus;
  /** Extra detail, e.g. "2 huecos · historial desde 12 sep". */
  detail?: React.ReactNode;
  className?: string;
}

/** Signals whether data completeness can be trusted. Disconnected never implies zero activity. */
export function CoverageIndicator({ status, detail, className }: CoverageIndicatorProps) {
  const c = COVERAGE[status];
  return (
    <span data-status={status} className={cn('inline-flex items-center gap-1.5 text-helper font-medium [&_svg]:size-4 [&_svg]:shrink-0', c.tone, className)}>
      {c.icon}
      <span>{c.label}</span>
      {detail ? <span className="font-normal text-text-secondary">· {detail}</span> : null}
    </span>
  );
}

export interface FreshnessLabelProps {
  /** When the figure was computed. null = never. */
  computedAt: Date | string | null | undefined;
  now?: Date;
  /** Older than this → stale styling. */
  staleAfterMinutes?: number;
  prefix?: string;
  className?: string;
}

export function FreshnessLabel({ computedAt, now = new Date(), staleAfterMinutes = 60, prefix = 'Calculado', className }: FreshnessLabelProps) {
  if (!computedAt) {
    return (
      <span className={cn('inline-flex items-center gap-1.5 text-helper text-text-secondary [&_svg]:size-4', className)}>
        <Clock aria-hidden />
        Aún no calculado
      </span>
    );
  }
  const t = computedAt instanceof Date ? computedAt : new Date(computedAt);
  const stale = now.getTime() - t.getTime() > staleAfterMinutes * 60_000;
  return (
    <span
      data-stale={stale || undefined}
      className={cn('inline-flex items-center gap-1.5 text-helper [&_svg]:size-4', stale ? 'font-medium text-warning' : 'text-text-secondary', className)}
    >
      {stale ? <AlertTriangle aria-hidden /> : <Clock aria-hidden />}
      <span className="cm-tabular">
        {prefix} {formatRelative(t, now)}
      </span>
      {stale ? <span>· desactualizado</span> : null}
    </span>
  );
}
